
import { karmaService } from './karmaService';
import { npcProfileService, NPCAchievement } from './npcProfileService';
import { v4 as uuidv4 } from 'uuid';
import { createClient, SupabaseClient } from '@supabase/supabase-js';

export interface FeedPost {
  id: string;
  authorId: string;
  authorType: 'player' | 'npc' | 'system';
  authorName: string;
  authorAvatar?: string;
  postType: 'achievement' | 'story_choice' | 'combat_result' | 'trade' | 'relationship' | 'world_event';
  title: string;
  content: string;
  imageUrl?: string;
  metadata: {
    cartridgeId?: string;
    cartridgeName?: string;
    genreId?: string;
    instanceId?: string;
    karmaImpact?: number;
    achievementType?: string;
    combatOutcome?: 'victory' | 'defeat' | 'draw';
    itemsTraded?: { given: string[]; received: string[] };
    relationshipChange?: { entityId: string; change: number };
  };
  engagement: {
    likes: string[]; // Array of user IDs who liked
    shares: string[]; // Array of user IDs who shared
    karmaRipples: number; // Total karma impact from engagement
  };
  visibility: 'public' | 'friends' | 'faction' | 'private';
  tags: string[];
  createdAt: Date;
  updatedAt: Date;
}

export interface FeedEngagement {
  postId: string;
  userId: string;
  action: 'like' | 'share' | 'react';
  reaction?: string; // For future emoji reactions
  timestamp: Date;
}

export interface WorldInfluence {
  threshold: number;
  postType: string;
  effect: 'genre_shift' | 'cartridge_mutation' | 'world_event' | 'faction_war' | 'karma_storm';
  description: string;
}

class FeedService {
  private worldInfluenceThresholds: WorldInfluence[] = [
    {
      threshold: 100,
      postType: 'combat_result',
      effect: 'faction_war',
      description: 'Too many combat posts trigger faction conflicts'
    },
    {
      threshold: 50,
      postType: 'achievement',
      effect: 'world_event',
      description: 'Mass achievements unlock special world events'
    },
    {
      threshold: 75,
      postType: 'story_choice',
      effect: 'cartridge_mutation',
      description: 'Collective choices alter cartridge narratives'
    },
    {
      threshold: 150,
      postType: 'relationship',
      effect: 'karma_storm',
      description: 'Relationship turmoil creates karma storms'
    }
  ];

  private postTypeCounters: Map<string, number> = new Map();

  constructor() {
    // Register with NPC service to receive achievements
    npcProfileService.onAchievement(this.handleNPCAchievement.bind(this));
  }

  /**
   * Create a new feed post
   */
  async createPost(
    post: Omit<FeedPost, 'id' | 'createdAt' | 'updatedAt' | 'engagement'>
  ): Promise<FeedPost> {
    const postId = uuidv4();
    const now = new Date();

    const feedPost: FeedPost = {
      ...post,
      id: postId,
      engagement: {
        likes: [],
        shares: [],
        karmaRipples: 0
      },
      createdAt: now,
      updatedAt: now
    };

    // Store in Supabase
    const { error } = await this.getSupabase()
      .from('feed_posts')
      .insert({
        id: postId,
        user_id: post.authorType === 'player' ? post.authorId : null,
        title: post.title,
        content: post.content,
        image_url: post.imageUrl,
        post_type: post.postType,
        metadata: feedPost,
        created_at: now
      });

    if (error) {
      console.error('[FeedService] Error creating feed post:', error);
      throw new Error('Failed to create feed post');
    }

    // Track post type for world influence
    this.trackPostType(post.postType);

    // Check for world influence triggers
    await this.checkWorldInfluence(post.postType);

    return feedPost;
  }

  /**
   * Create achievement post for players
   */
  async createPlayerAchievementPost(
    playerId: string,
    playerName: string,
    achievement: {
      type: string;
      title: string;
      description: string;
      imageUrl?: string;
      karmaImpact?: number;
    },
    cartridgeInfo?: { id: string; name: string; genre: string; instanceId: string }
  ): Promise<void> {
    await this.createPost({
      authorId: playerId,
      authorType: 'player',
      authorName: playerName,
      postType: 'achievement',
      title: achievement.title,
      content: achievement.description,
      imageUrl: achievement.imageUrl,
      metadata: {
        achievementType: achievement.type,
        karmaImpact: achievement.karmaImpact,
        cartridgeId: cartridgeInfo?.id,
        cartridgeName: cartridgeInfo?.name,
        genreId: cartridgeInfo?.genre,
        instanceId: cartridgeInfo?.instanceId
      },
      visibility: 'public',
      tags: [achievement.type, 'achievement', playerName]
    });
  }

  /**
   * Create story choice post
   */
  async createStoryChoicePost(
    playerId: string,
    playerName: string,
    choice: {
      text: string;
      consequence: string;
      karmaImpact: number;
      imageUrl?: string;
    },
    cartridgeInfo: { id: string; name: string; genre: string; instanceId: string }
  ): Promise<void> {
    await this.createPost({
      authorId: playerId,
      authorType: 'player',
      authorName: playerName,
      postType: 'story_choice',
      title: `${playerName} made a pivotal choice`,
      content: `Chose: "${choice.text}"\n\n${choice.consequence}`,
      imageUrl: choice.imageUrl,
      metadata: {
        karmaImpact: choice.karmaImpact,
        cartridgeId: cartridgeInfo.id,
        cartridgeName: cartridgeInfo.name,
        genreId: cartridgeInfo.genre,
        instanceId: cartridgeInfo.instanceId
      },
      visibility: 'public',
      tags: ['story', 'choice', cartridgeInfo.name]
    });

    // Apply karma ripples based on choice
    if (Math.abs(choice.karmaImpact) > 10) {
      await karmaService.applyKarmaRipples(
        'story_choice',
        playerId,
        choice.karmaImpact,
        [playerId],
        2
      );
    }
  }

  /**
   * Create combat result post
   */
  async createCombatResultPost(
    participantId: string,
    participantName: string,
    participantType: 'player' | 'npc',
    combat: {
      outcome: 'victory' | 'defeat' | 'draw';
      opponent: string;
      karmaGained: number;
      experienceGained: number;
    }
  ): Promise<void> {
    const outcomeEmoji = combat.outcome === 'victory' ? '⚔️' : 
                        combat.outcome === 'defeat' ? '💀' : '🤝';
    
    await this.createPost({
      authorId: participantId,
      authorType: participantType,
      authorName: participantName,
      postType: 'combat_result',
      title: `${participantName} ${combat.outcome} against ${combat.opponent} ${outcomeEmoji}`,
      content: `Karma: ${combat.karmaGained > 0 ? '+' : ''}${combat.karmaGained}\nExperience: +${combat.experienceGained}`,
      metadata: {
        combatOutcome: combat.outcome,
        karmaImpact: combat.karmaGained
      },
      visibility: 'public',
      tags: ['combat', combat.outcome, participantName]
    });
  }

  /**
   * Handle NPC achievements
   */
  private async handleNPCAchievement(achievement: NPCAchievement): Promise<void> {
    const npc = await npcProfileService.getNPCProfile(achievement.npcId);
    if (!npc) return;

    await this.createPost({
      authorId: achievement.npcId,
      authorType: 'npc',
      authorName: npc.name,
      postType: 'achievement',
      title: achievement.title,
      content: achievement.description,
      imageUrl: achievement.imageUrl,
      metadata: {
        achievementType: achievement.type,
        karmaImpact: achievement.karmaValue,
        ...achievement.metadata
      },
      visibility: 'public',
      tags: [achievement.type, 'npc', npc.name]
    });
  }

  /**
   * Engage with a post (like/share)
   */
  async engageWithPost(
    postId: string,
    userId: string,
    action: 'like' | 'share'
  ): Promise<void> {
    const { data: postData, error: fetchError } = await this.getSupabase()
      .from('feed_posts')
      .select('metadata')
      .eq('id', postId)
      .single();

    if (fetchError || !postData) {
      console.error('[FeedService] Error fetching post:', fetchError);
      return;
    }

    const post = postData.metadata as FeedPost;

    // Update engagement
    if (action === 'like' && !post.engagement.likes.includes(userId)) {
      post.engagement.likes.push(userId);
      post.engagement.karmaRipples += 1;
    } else if (action === 'share' && !post.engagement.shares.includes(userId)) {
      post.engagement.shares.push(userId);
      post.engagement.karmaRipples += 3;
    }

    // Update post
    const { error: updateError } = await this.getSupabase()
      .from('feed_posts')
      .update({
        metadata: post,
        updated_at: new Date()
      })
      .eq('id', postId);

    if (updateError) {
      console.error('[FeedService] Error updating post engagement:', updateError);
      return;
    }

    // Update trending score
    const score = post.engagement.likes.length + post.engagement.shares.length * 2;
    await this.getSupabase().from('trending_posts').upsert({ post_id: postId, score }, { onConflict: 'post_id' });

    // Apply karma ripples from engagement
    if (post.engagement.karmaRipples % 10 === 0) {
      await karmaService.applyKarmaRipples(
        'social_engagement',
        post.authorId,
        Math.floor(post.engagement.karmaRipples / 10),
        [userId, post.authorId],
        1
      );
    }

    // Check for viral posts triggering world events
    const totalEngagement = post.engagement.likes.length + post.engagement.shares.length;
    if (totalEngagement >= 50) {
      await this.triggerViralPostEffect(post);
    }
  }

  /**
   * Get feed for a user (with filters)
   */
  async getUserFeed(
    userId: string,
    filters?: {
      postTypes?: string[];
      authorTypes?: ('player' | 'npc' | 'system')[];
      cartridgeId?: string;
      genreId?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<FeedPost[]> {
    let query = this.getSupabase()
      .from('feed_posts')
      .select('*')
      .order('created_at', { ascending: false });

    if (filters?.postTypes) {
      query = query.in('post_type', filters.postTypes);
    }

    if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    if (filters?.offset) {
      query = query.range(filters.offset, filters.offset + (filters.limit || 20) - 1);
    }

    const { data, error } = await query;

    if (error) {
      console.error('[FeedService] Error fetching feed:', error);
      return [];
    }

    // Filter by additional criteria and transform to FeedPost
    return data
      .map(row => row.metadata as FeedPost)
      .filter(post => {
        if (filters?.authorTypes && !filters.authorTypes.includes(post.authorType)) {
          return false;
        }
        if (filters?.cartridgeId && post.metadata.cartridgeId !== filters.cartridgeId) {
          return false;
        }
        if (filters?.genreId && post.metadata.genreId !== filters.genreId) {
          return false;
        }
        return true;
      });
  }

  /**
   * Get trending posts (most engaged)
   */
  async getTrendingPosts(limit: number = 10): Promise<FeedPost[]> {
    const { data, error } = await this.getSupabase()
      .from('trending_posts')
      .select('feed_posts (*)')
      .order('score', { ascending: false })
      .limit(limit);

    if (error || !data) {
      console.error('[FeedService] Error fetching trending posts:', error);
      return [];
    }

    return data.map(row => (row.feed_posts as any).metadata as FeedPost);
  }

  /**
   * Track post types for world influence
   */
  private trackPostType(postType: string): void {
    const current = this.postTypeCounters.get(postType) || 0;
    this.postTypeCounters.set(postType, current + 1);
  }

  /**
   * Check if post type threshold triggers world influence
   */
  private async checkWorldInfluence(postType: string): Promise<void> {
    const count = this.postTypeCounters.get(postType) || 0;
    
    for (const influence of this.worldInfluenceThresholds) {
      if (postType === influence.postType && count >= influence.threshold) {
        await this.triggerWorldInfluence(influence);
        // Reset counter after triggering
        this.postTypeCounters.set(postType, 0);
      }
    }
  }

  /**
   * Trigger world influence event
   */
  private async triggerWorldInfluence(influence: WorldInfluence): Promise<void> {
    console.log('[FeedService] World Influence Triggered:', influence);

    // Create system post about world event
    await this.createPost({
      authorId: 'system',
      authorType: 'system',
      authorName: 'The Dream Weaver',
      postType: 'world_event',
      title: `🌍 World Event: ${influence.effect}`,
      content: influence.description,
      metadata: {
        karmaImpact: influence.threshold
      },
      visibility: 'public',
      tags: ['world_event', influence.effect]
    });

    // Update world state based on influence
    switch (influence.effect) {
      case 'karma_storm':
        await this.triggerKarmaStorm();
        break;
      case 'faction_war':
        await this.triggerFactionWar();
        break;
      case 'cartridge_mutation':
        await this.triggerCartridgeMutation();
        break;
      case 'genre_shift':
        await this.triggerGenreShift();
        break;
      case 'world_event':
        await this.triggerSpecialWorldEvent();
        break;
    }
  }

  /**
   * Handle viral post effects
   */
  private async triggerViralPostEffect(post: FeedPost): Promise<void> {
    console.log('[FeedService] Viral post detected:', post.title);

    // Apply massive karma ripples
    await karmaService.applyKarmaRipples(
      'viral_post',
      post.authorId,
      50,
      [...post.engagement.likes, ...post.engagement.shares],
      7 // Maximum ripple distance
    );

    // Create follow-up system post
    await this.createPost({
      authorId: 'system',
      authorType: 'system',
      authorName: 'The Collective Unconscious',
      postType: 'world_event',
      title: '🌟 Viral Phenomenon Detected',
      content: `"${post.title}" has captured the collective consciousness, sending ripples through the dream realm.`,
      metadata: {
        karmaImpact: 50,
        relatedPostId: post.id
      },
      visibility: 'public',
      tags: ['viral', 'collective_consciousness']
    });
  }

  // World influence effect implementations
  private async triggerKarmaStorm(): Promise<void> {
    // Update world state to karma storm
    await this.getSupabase()
      .from('world_state')
      .update({ state: 'karma_storm' })
      .eq('id', 'current');
  }

  private async triggerFactionWar(): Promise<void> {
    console.log('[FeedService] Faction war triggered by excessive combat');
    // TODO: Implement faction war mechanics
  }

  private async triggerCartridgeMutation(): Promise<void> {
    console.log('[FeedService] Cartridge mutation triggered by collective choices');
    // TODO: Implement cartridge mutation mechanics
  }

  private async triggerGenreShift(): Promise<void> {
    console.log('[FeedService] Genre shift triggered by collective actions');
    // TODO: Implement genre shift mechanics
  }

  private async triggerSpecialWorldEvent(): Promise<void> {
    console.log('[FeedService] Special world event triggered');
    // TODO: Implement special world events
  }
}

export const feedService = new FeedService();